// LessonPlanOutputPanel.jsx
import React, { memo } from "react";
import { FaBookOpen, FaFilePdf, FaClipboardList } from "react-icons/fa";
import { CustomButton } from "../../../../components";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import QuillEditor from "../../../../components/QuillEditor/QuillEditor";
import SavedLessonPlanCard from "../../../../components/SavedLessonPlanCard/SavedLessonPlanCard";
import academicStyles from "../../../schoolCockpit/master/academicConfiguration/AcademicCreate.module.css";
import styles from "../../../schoolCockpit/master/academicConfiguration/Academiclist.module.css";

const LessonPlanOutputPanel = memo(function LessonPlanOutputPanel({
  t,
  isEditMode,
  editingCardHmPk,
  // PDF + editor
  pdfUrl,
  pdfFromPage,
  pdfToPage,
  lessonPlanContent,
  onLessonContentChange,
  generating,
  // Saved cards
  savedLessonPlans,
  selectedCardId,
  disableScheduleGlobally,
  onCardSelect,
  onCardPin,
  onEditCard,
  onDeleteCard,
  onScheduleCard,
  // Footer
  onCancel,
  onSaveDraft,
  onPrimary,
  showSaveAsDraft,
  saveAsDraftDisabled,
  submitDisabled,
  primaryLabel,
}) {
  return (
    <>
      {/* ============ MIDDLE: PDF + Editor ============ */}
      <div className="row" style={{ marginTop: 16 }}>
        <div className="col-lg-6 col-md-12 px-2">
          <div className={academicStyles.container} style={{ height: "100%" }}>
            <div className={academicStyles.header}>
              <div className={academicStyles.headerIcon}>
                <FaFilePdf aria-hidden />
              </div>
              <h2 className={academicStyles.headerTitle}>
                {t.bookPdfTitle || "Book PDF"}
              </h2>
            </div>
            <PdfViewer
              file={pdfUrl}
              fromPage={pdfFromPage}
              toPage={pdfToPage}
              fallbackMessage={t.pdfFallbackMessage || "PDF will appear here"}
              enableFullscreen
            />
          </div>
        </div>

        <div className="col-lg-6 col-md-12 px-2">
          <div className={academicStyles.container} style={{ height: "100%" }}>
            <div className={academicStyles.header}>
              <div className={academicStyles.headerIcon}>
                <FaBookOpen aria-hidden />
              </div>
              <h2 className={academicStyles.headerTitle}>
                {t.lessonPlan || "Lesson Plan"}
              </h2>
            </div>
            <div style={{ position: "relative" }}>
              <QuillEditor
                value={lessonPlanContent}
                onChange={onLessonContentChange}
                placeholder={
                  t.lessonPlanEditorPlaceholder ||
                  "Lesson plan will appear here after generation..."
                }
                minHeight={400}
                maxHeight={600}
                readOnly={generating}
                showWordCount={false}
              />
              {generating && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(255,255,255,0.85)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    borderRadius: "var(--border-radius-md)",
                    zIndex: 5,
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      border: "3px solid var(--border-light, #e5e7eb)",
                      borderTopColor: "var(--color-primary)",
                      borderRadius: "50%",
                      animation: "lp-spin 0.9s linear infinite",
                    }}
                  />
                  <div
                    style={{
                      color: "var(--text-muted, #6b7280)",
                      fontSize: "var(--font-size-sm, 14px)",
                    }}
                  >
                    {t.generatingWithAi || "Generating with Gemini AI"}
                  </div>
                  <style>{`@keyframes lp-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ BOTTOM: Saved Cards ============ */}
      {savedLessonPlans.length > 0 && (
        <div className={academicStyles.container} style={{ marginTop: 16 }}>
          <div className={academicStyles.header}>
            <div className={academicStyles.headerIcon}>
              <FaClipboardList aria-hidden />
            </div>
            <h2 className={academicStyles.headerTitle}>
              {t.savedLessonPlan || "Saved Lesson Plan"}
            </h2>
          </div>

          <div className={`row ${styles.savedCardsRow}`}>
            {savedLessonPlans.map((plan) => (
              <div key={plan.id} className="col-lg-4 col-md-6 col-sm-12 col-12">
                <SavedLessonPlanCard
                  id={plan.id}
                  title={plan.title}
                  date={plan.date}
                  time={plan.time}
                  status={plan.status || "Draft"}
                  isSelected={selectedCardId === plan.id}
                  isPinned={!!plan.is_pinned}
                  disableScheduleGlobally={disableScheduleGlobally}
                  onSelect={onCardSelect}
                  onPin={onCardPin}
                  onEdit={() => onEditCard(plan)}
                  onDelete={() => onDeleteCard(plan.id)}
                  onSchedule={onScheduleCard}
                  translations={t}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ FOOTER ============ */}
      <div
        className={academicStyles.actions}
        style={{ justifyContent: "center", marginTop: 24, gap: 16 }}
      >
        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.cancelButton}
          fullWidth={false}
          onClick={onCancel}
        >
          {t.cancelButton || "Cancel"}
        </CustomButton>

        {showSaveAsDraft && (
          <CustomButton
            type="button"
            variant="contained"
            className={academicStyles.addButton}
            fullWidth={false}
            onClick={onSaveDraft}
            disabled={saveAsDraftDisabled}
          >
            {t.saveAsDraft || "Save as Draft"}
          </CustomButton>
        )}

        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.addButton}
          fullWidth={false}
          onClick={onPrimary}
          disabled={submitDisabled}
        >
          {primaryLabel}
        </CustomButton>
      </div>
    </>
  );
});

export default LessonPlanOutputPanel;
